const LAMBDA_URL = process.env.REACT_APP_PDF_LAMBDA_URL || 'https://5faheloygah3m7jhlp6nv7ma7e0bghrz.lambda-url.ap-southeast-1.on.aws/';

/**
 * Generate and download PDF for a filing using AWS Lambda + Puppeteer
 * @param filingId - The filing ID
 * @param companyInfo - Optional company info for filename generation (submission_type, company_number, reporting_end)
 * @returns Promise that resolves when PDF download starts
 */
export const downloadFilingPDF = async (
  filingId: string,
  companyInfo?: {
    submission_type?: string;
    company_number?: string;
    reporting_end?: string;
  }
): Promise<void> => {
  try {
    console.log(`📄 Generating PDF for filing: ${filingId}`);
    console.log(`📡 PDF Lambda URL: ${LAMBDA_URL}`);
    
    // Capture rendered HTML from ViewFilings page
    const viewContent = document.querySelector('.view-filing-page');
    
    if (!viewContent) {
      throw new Error('View content not found - are you on the ViewFilings page?');
    }
    
    // Collect ALL styles (inline + computed from stylesheets)
    const styleElements = Array.from(document.querySelectorAll('style'))
      .map(style => style.innerHTML)
      .join('\n');
    
    // Get CSS from all loaded stylesheets (convert to inline for Lambda)
    const linkedStyles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          // CORS-blocked stylesheets
          return '';
        }
      })
      .join('\n');
    
    // Build complete HTML document with ALL styles inline
    // 🔑 NO extra CSS overrides here - view-filing.css @media print rules are the single source of truth
    const fullHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Filing Report</title>
  <style>
    /* Inline styles from document */
    ${styleElements}
    
    /* Stylesheet rules converted to inline (includes @media print from view-filing.css) */
    ${linkedStyles}
  </style>
</head>
<body>
  ${viewContent.innerHTML}
</body>
</html>
    `;
    
    console.log(`📄 Captured HTML (${fullHTML.length} characters)`);
    
    // Call Lambda function with HTML and company info
    const response = await fetch(LAMBDA_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        filing_id: filingId,
        html: fullHTML,
        company_info: companyInfo
      })
    });
    
    console.log(`📡 Response status: ${response.status}`);
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Error response:', errorText);
      
      let error;
      try {
        error = JSON.parse(errorText);
      } catch {
        throw new Error(`Server error: ${response.status} ${response.statusText}`);
      }
      
      throw new Error(error.details || error.error || 'PDF generation failed');
    }
    
    // Get PDF blob
    const blob = await response.blob();
    console.log(`📦 PDF size: ${blob.size} bytes`);
    
    // Extract filename from Content-Disposition header
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = `filing_${filingId}.pdf`;
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    
    console.log(`📄 Filename: ${filename}`);
    
    // Trigger download
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    console.log('✅ PDF downloaded successfully');
    
  } catch (error) {
    console.error('❌ PDF generation error:', error);
    throw error;
  }
};

